import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Navbar from '../components/Navbar';
import { 
  PaperAirplaneIcon, 
  ArrowPathIcon, 
  ChatBubbleLeftRightIcon,
  EllipsisVerticalIcon,
  PlusIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { CourseComparisonModal } from '../components/CourseComparisonModal';
import CourseCard from '../components/CourseCard';
import { useAuth } from '../context/AuthContext';
import { createApiUrl } from '../config/api';

/**
 * Safely converts an object with NaN values to a JSON-serializable object
 * @param {Object} obj - The object to sanitize
 * @returns {Object} - A new object with all NaN values replaced with null
 */
const sanitizeForJSON = (obj) => {
  // Handle null, undefined, or primitive values
  if (obj === null || obj === undefined) return null;
  if (typeof obj !== 'object') {
    // Handle NaN special case for numbers
    return Number.isNaN(obj) ? null : obj;
  }
  
  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeForJSON(item));
  }
  
  // Handle objects
  const result = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];
      if (value !== value) { // This is how you check for NaN in JavaScript
        result[key] = null;
      } else if (typeof value === 'object') {
        result[key] = sanitizeForJSON(value);
      } else {
        result[key] = value;
      }
    }
  }
  return result;
};

function Chat() {
  const navigate = useNavigate();
  const { currentUser, authProvider } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [hasProfile, setHasProfile] = useState(false);
  const [compareModalOpen, setCompareModalOpen] = useState(false);
  const [coursesToCompare, setCoursesToCompare] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  // Enterprise chat session management
  const [chatSessions, setChatSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [isSubmittingMessage, setIsSubmittingMessage] = useState(false);
  const [hasLoadedInitially, setHasLoadedInitially] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      console.log('=== USEEFFECT FETCHDATA RUNNING ===');
      console.log('currentUser:', currentUser);
      console.log('isSubmittingMessage:', isSubmittingMessage);
      
      try {
        // Check if user is authenticated via AuthContext
        if (!currentUser) {
          console.log('No currentUser, navigating to login');
          navigate('/login');
          return;
        }
        
        console.log('Fetching profile...');
        const profileRes = await axios.get(createApiUrl('api/profile'));
        
        // Start with empty messages for new session - don't load old chat history automatically
        console.log('=== USEEFFECT STARTING FRESH ===');
        console.log('Starting with empty messages for new session');
        setMessages([]);
        setCurrentSessionId(null); // Ensure no session is pre-loaded
        const profile = profileRes.data;
        const hasInfo = profile.concentration && profile.year;
        setHasProfile(hasInfo);
        if (!hasInfo) navigate('/profile');
        
        // Load chat sessions from the API
        try {
          const sessionsRes = await axios.get(createApiUrl('api/chat/sessions'));
          setChatSessions(sessionsRes.data.sessions || []);
        } catch (err) {
          console.error('Failed to load chat sessions:', err);
          setChatSessions([]);
        }
        
      } catch (err) {
        console.error(err);
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    
    // Only run ONCE on initial load, never again to avoid overwriting messages
    if (currentUser !== undefined && !hasLoadedInitially) {
      setHasLoadedInitially(true);
      fetchData();
    }
  }, [navigate, currentUser, hasLoadedInitially]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const isCourseInfoRequest = (text) => {
    const coursePattern = /([A-Za-z]{2,4})\s*(\d{1,3}[A-Za-z]*)/i;
    const lowerText = text.toLowerCase();
    
    // Return true if the text contains a course code and either:
    // 1. Contains one of the specific request phrases OR
    // 2. Contains "course" and one of these related terms: "card", "info", "details", "information"
    return coursePattern.test(text) &&
      !lowerText.includes('compare') &&
      (
        /(what is|tell me about|info on|details about|course information|show me|display)/i.test(text) ||
        (
          lowerText.includes('course') && 
          /(card|info|details|information|description|overview)/i.test(lowerText)
        )
      );
  };

  const extractCourseCode = (text) => {
    const match = text.match(/([A-Za-z]{2,4})\s*(\d{1,3}[A-Za-z]*)/i);
    return match ? `${match[1].toUpperCase()} ${match[2]}` : null;
  };

    // Helper function to replace NaN values with null for JSON serialization
  // Helper function to replace NaN values with null for JSON serialization
const sanitizeForJSON = (obj) => {
  if (obj === null || obj === undefined) return null;
  
  // Handle NaN specifically
  if (typeof obj === 'number' && isNaN(obj)) return null;
  
  if (typeof obj !== 'object') return obj;
  
  // If it's an array, process each element
  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeForJSON(item));
  }
  
  // Process object properties
  const result = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];
      
      // Check specifically for NaN
      if (typeof value === 'number' && isNaN(value)) {
        result[key] = null;
      } else if (typeof value === 'object') {
        result[key] = sanitizeForJSON(value);
      } else {
        result[key] = value;
      }
    }
  }
  return result;
};

  // This is the updated part of your handleSubmit function that handles course data
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || sending || sessionLoading) return;
    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...(prev || []), { role: 'user', content: userMessage }]);
    setSending(true);
    setIsSubmittingMessage(true);

    try {
      // Check if this is a course info request
      let courseData = null;
      const isCourseReq = isCourseInfoRequest(userMessage);
      
      if (isCourseReq) {
        const code = extractCourseCode(userMessage);
        if (code) {
          try {
            const courseRes = await axios.get(createApiUrl(`api/courses/${code}`));
            courseData = sanitizeForJSON(courseRes.data);
          } catch (err) {
            console.error('Failed to fetch course data:', err);
          }
        }
      }

      // Get the AI response
      console.log('=== CHAT SUBMIT START ===');
      console.log('User message:', userMessage);
      console.log('Current messages before API:', messages);
      
      // Force new session if this is the first message (no existing messages)
      const isFirstMessage = !messages || messages.length === 0;
      
      const response = await axios.post(createApiUrl('api/chat/message'), { 
        message: userMessage,
        provider: authProvider || 'openai',
        force_new_session: isFirstMessage
      });
      
      console.log('=== API RESPONSE ===');
      console.log('Raw response:', response);
      console.log('Response data type:', typeof response.data);
      
      // Parse JSON if response.data is a string
      let responseData = response.data;
      if (typeof response.data === 'string') {
        console.log('Parsing JSON string...');
        const cleanedJsonString = response.data.replace(/:\s*NaN/g, ': null');
        responseData = JSON.parse(cleanedJsonString);
      }
      
      console.log('=== PARSED DATA ===');
      console.log('Response data:', responseData);
      console.log('History array length:', responseData.history?.length);
      console.log('History preview:', responseData.history?.slice(0, 2));
      
      let updated = Array.isArray(responseData.history) ? responseData.history : [];
      console.log('Updated messages to set:', updated.length);
      
      console.log('=== BEFORE setMessages ===');
      console.log('About to call setMessages with:', updated.length, 'messages');

      // Handle comparison request
      if (userMessage.toLowerCase().includes('compare')) {
        // Extract course codes for comparison
        const courseCodeMatches = userMessage.match(/([A-Za-z]{2,4})\s*(\d{1,3}[A-Za-z]*)/g);
        if (courseCodeMatches && courseCodeMatches.length >= 2) {
          try {
            // Fetch data for all mentioned courses
            const coursePromises = courseCodeMatches.slice(0, 4).map(async (courseMatch) => {
              const cleanCode = courseMatch.replace(/\s+/g, ' ').trim();
              try {
                const res = await axios.get(createApiUrl(`api/courses/${cleanCode}`));
                return sanitizeForJSON(res.data);
              } catch (err) {
                console.error(`Failed to fetch ${cleanCode}:`, err);
                return null;
              }
            });
            
            const coursesData = await Promise.all(coursePromises);
            const validCourses = coursesData.filter(course => course && course.class_tag && course.class_name);
            
            if (validCourses.length >= 2) {
              // Open comparison modal
              setCoursesToCompare(validCourses);
              setCompareModalOpen(true);
            }
          } catch (err) {
            console.error('Failed to fetch courses for comparison:', err);
          }
        }
      } 
      // If we have course data, add it to the assistant's message
      else if (courseData) {
        const lastAssistantMsgIndex = [...updated].reverse().findIndex(msg => msg.role === 'assistant');
        if (lastAssistantMsgIndex !== -1) {
          const trueIndex = updated.length - 1 - lastAssistantMsgIndex;
          updated[trueIndex] = {
            ...updated[trueIndex],
            courseData: courseData,
          };
        }
      }
      // Check if AI response mentions course codes and fetch their data
      else {
        const lastAssistantMsgIndex = [...updated].reverse().findIndex(msg => msg.role === 'assistant');
        if (lastAssistantMsgIndex !== -1) {
          const trueIndex = updated.length - 1 - lastAssistantMsgIndex;
          const assistantMessage = updated[trueIndex];
          
          // Extract course codes from AI response (case-insensitive, broader pattern)
          const courseCodeMatches = assistantMessage.content.match(/([A-Za-z]{2,4})\s+\d{1,3}[A-Za-z]*/gi);
          console.log('🔍 Detected course codes:', courseCodeMatches);
          if (courseCodeMatches && courseCodeMatches.length > 0) {
            // Check if response mentions multiple courses (comparison scenario)
            if (courseCodeMatches.length >= 2) {
              // For multiple courses, attach array of course data
              try {
                const coursePromises = courseCodeMatches.slice(0, 3).map(async (courseMatch) => {
                  const cleanCode = courseMatch.replace(/\s+/g, ' ').toUpperCase();
                  try {
                    const res = await axios.get(createApiUrl(`api/courses/${cleanCode}`));
                    return sanitizeForJSON(res.data);
                  } catch (err) {
                    return null;
                  }
                });
                
                const coursesData = await Promise.all(coursePromises);
                const validCourses = coursesData.filter(course => course && course.class_tag && course.class_name);
                
                if (validCourses.length > 0) {
                  updated[trueIndex] = {
                    ...updated[trueIndex],
                    multipleCourseData: validCourses,
                  };
                  
                  // Store courses for modal after setMessages
                  if (validCourses.length >= 2) {
                    // Use setTimeout to ensure modal opens after state update
                    setTimeout(() => {
                      setCoursesToCompare(validCourses);
                      setCompareModalOpen(true);
                    }, 100);
                  }
                }
              } catch (err) {
                console.error('Failed to fetch multiple course data:', err);
              }
            } else {
              // Single course mentioned
              const firstCourseCode = courseCodeMatches[0].replace(/\s+/g, ' ').toUpperCase();
              try {
                const courseRes = await axios.get(createApiUrl(`api/courses/${firstCourseCode}`));
                const fetchedCourseData = sanitizeForJSON(courseRes.data);
                updated[trueIndex] = {
                  ...updated[trueIndex],
                  courseData: fetchedCourseData,
                };
              } catch (err) {
                console.error('Failed to fetch mentioned course data:', err);
              }
            }
          }
        }
      }

      setMessages(updated);
      console.log('=== AFTER setMessages ===');
      console.log('setMessages called successfully');
    } catch (err) {
      console.error('Chat API Error:', err.message);
      
      const errorMessage = err.response?.status === 401 
        ? 'Authentication failed. Please log in again.' 
        : err.response?.status === 404
        ? 'API endpoint not found. Check server status.'
        : 'Sorry, I ran into an error. Try again!';
        
      setMessages(prev => [...(prev || []), { 
        role: 'assistant', 
        content: errorMessage
      }]);
    } finally {
      console.log('=== FINALLY BLOCK ===');
      setSending(false);
      setIsSubmittingMessage(false);
      inputRef.current?.focus();
      console.log('Finally block completed');
    }
  };

  const handleClearChat = async () => {
    try {
      await axios.post(createApiUrl('api/chat/clear'));
      setMessages([]);
    } catch (err) {
      console.error(err);
    }
  };

  const handleNewChat = async () => {
    try {
      setSessionLoading(true); // Prevent new messages during transition
      
      // Only create new chat if we have messages to save
      if (messages && messages.length > 0) {
        // Start new chat session on backend (this saves current session automatically)
        await axios.post(createApiUrl('api/chat/clear'));
        
        // Reload chat sessions to show the saved session
        const sessionsRes = await axios.get(createApiUrl('api/chat/sessions'));
        setChatSessions(sessionsRes.data.sessions || []);
      }
      
      // Clear frontend state for new conversation
      setMessages([]);
      setCurrentSessionId(null);
      
      // Clear input
      if (inputRef.current) {
        inputRef.current.value = '';
      }
      
      // Focus on input for new conversation
      setTimeout(() => {
        inputRef.current?.focus();
        setSessionLoading(false); // Re-enable messages after transition
      }, 200);
      
    } catch (err) {
      console.error('Error starting new chat:', err);
      setSessionLoading(false); // Re-enable on error
    }
  };

  const normalizeCourseData = (data) => {
    if (!data) {
      return {};
    }
    
    // Convert the data to a usable object
    let courseData = data;
    
    // If it's a string, we need to parse it, but we need to handle NaN values
    if (typeof data === 'string') {
      try {
        // Replace NaN in the string with null before parsing
        const cleanedString = data
          .replace(/:\s*NaN/g, ': null')
          .replace(/:\s*"NaN"/g, ': null');
        
        courseData = JSON.parse(cleanedString);
      } catch (e) {
        console.error("Failed to parse course data string:", e);
        // Try a different approach
        try {
          // Try to evaluate the object directly (careful with this approach)
          // This is needed because JSON.parse doesn't accept NaN
          courseData = eval(`(${data})`);
          // Immediately sanitize to replace any NaN values
          courseData = sanitizeForJSON(courseData);
        } catch (e2) {
          console.error("Failed second parsing attempt:", e2);
          return {}; // Give up and return empty object
        }
      }
    }
    
    
    // Extract basic fields with safety checks
    const safeString = (field) => {
      const val = courseData[field];
      if (val === null || val === undefined || Number.isNaN(val)) return '';
      return String(val).trim();
    };
    
    const safeNumber = (field) => {
      const val = courseData[field];
      if (val === null || val === undefined || Number.isNaN(val)) return null;
      const num = parseFloat(val);
      return isNaN(num) ? null : num;
    };
    
    const parseJsonArray = (field) => {
      try {
        const val = courseData[field];
        if (!val) return [];
        
        // Already an array
        if (Array.isArray(val)) return val;
        
        // String that needs parsing
        if (typeof val === 'string') {
          return JSON.parse(val);
        }
        
        return [];
      } catch (e) {
        console.warn(`Failed to parse ${field} as array:`, e);
        return [];
      }
    };
    
    // Extract core fields
    const code = safeString('class_tag');
    const title = safeString('class_name');
    const description = safeString('description');
    const instructors = parseJsonArray('instructors');
    const instructorStr = Array.isArray(instructors) ? instructors.join(', ') : '';
    const days = parseJsonArray('days');
    const daysStr = Array.isArray(days) ? days.join(', ') : '';
    
    // Build the normalized course object
    const normalized = {
      code: code,
      title: title,
      description: description,
      instructor: instructorStr, 
      term: safeString('term'),
      rating: safeNumber('overall_score_course_mean'),
      workload: safeNumber('mean_hours'),
      prerequisites: [],
      enrollment: safeNumber('current_enrollment_number'),
      tags: [],
      schedule: daysStr,
      qReportLink: safeString('q_report'),
      units: safeString('units')
    };
    
    // Add prerequisites
    if (courseData.recommended_prep) {
      normalized.prerequisites.push(courseData.recommended_prep);
    }
    
    if (courseData.course_requirements) {
      normalized.prerequisites.push(courseData.course_requirements);
    }
    
    // Add basic tags
    if (courseData.department) {
      normalized.tags.push(courseData.department);
    }
    
    
    // Log warning if essential fields are missing
    if (!normalized.code || !normalized.title) {
      console.warn('⚠️ Missing key course fields:', normalized);
    }
    
    return normalized;
  };  

  const renderMessageContent = (message) => {
    if (message.role === 'assistant' && (message.courseData || message.multipleCourseData)) {
      return (
        <>
          <ReactMarkdown remarkPlugins={[remarkGfm]} className="prose dark:prose-dark mb-3">
            {message.content}
          </ReactMarkdown>
          
          {/* Single course card */}
          {message.courseData && (() => {
            const normalized = normalizeCourseData(message.courseData);
            const hasEssentialData = normalized && normalized.code && normalized.title;
            
            return hasEssentialData ? (
              <div className="border border-accent-primary/20 dark:border-accent-primary/30 p-4 my-4 rounded-lg shadow-sm bg-white dark:bg-dark-200">
                <CourseCard 
                  course={normalized} 
                  className="course-card-in-chat"
                />
                {normalized.qReportLink && (
                  <div className="text-xs text-gray-500 mt-2 text-right">
                    <a 
                      href={normalized.qReportLink} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-accent-primary dark:text-accent-secondary hover:underline"
                    >
                      View Q Report
                    </a>
                  </div>
                )}
              </div>
            ) : null;
          })()}
          
          {/* Multiple course cards */}
          {message.multipleCourseData && message.multipleCourseData.length > 0 && (
            <div className="space-y-4 my-4">
              {message.multipleCourseData.map((courseData, index) => {
                const normalized = normalizeCourseData(courseData);
                const hasEssentialData = normalized && normalized.code && normalized.title;
                
                return hasEssentialData ? (
                  <div key={index} className="border border-accent-primary/20 dark:border-accent-primary/30 p-4 rounded-lg shadow-sm bg-white dark:bg-dark-200">
                    <CourseCard 
                      course={normalized} 
                      className="course-card-in-chat"
                    />
                    {normalized.qReportLink && (
                      <div className="text-xs text-gray-500 mt-2 text-right">
                        <a 
                          href={normalized.qReportLink} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-accent-primary dark:text-accent-secondary hover:underline"
                        >
                          View Q Report
                        </a>
                      </div>
                    )}
                  </div>
                ) : null;
              })}
              
              {/* Comparison button if multiple courses */}
              {message.multipleCourseData.length >= 2 && (
                <button
                  onClick={() => {
                    setCoursesToCompare(message.multipleCourseData);
                    setCompareModalOpen(true);
                  }}
                  className="w-full mt-4 px-4 py-2 bg-accent-primary hover:bg-accent-tertiary text-white rounded-lg transition-colors duration-200 text-sm font-medium"
                >
                  Compare These Courses
                </button>
              )}
            </div>
          )}
        </>
      );
    }
    
    return (
      <ReactMarkdown remarkPlugins={[remarkGfm]} className="prose dark:prose-dark">
        {message.content}
      </ReactMarkdown>
    );
  };

  if (!hasProfile) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-50 to-gray-100 dark:from-dark-100 dark:via-dark-200 dark:to-dark-100 font-sans antialiased">
      <Navbar />
      
      {/* Enterprise Chat Layout */}
      <div className="flex h-[calc(100vh-3rem)]">
        {/* Modern Sidebar */}
        <div className={`hidden lg:flex ${sidebarCollapsed ? 'w-16' : 'w-60'} border-r border-dark-400/20 bg-dark-100/95 backdrop-blur-sm transition-all duration-300`}>
          <div className="flex flex-col w-full">
            {/* Sidebar Header with Toggle */}
            <div className="p-4 border-b border-dark-400/20">
              <div className="flex items-center justify-between">
                {!sidebarCollapsed && (
                  <button
                    onClick={handleNewChat}
                    className="flex items-center gap-2 px-3 py-2 bg-dark-200/80 hover:bg-dark-300/80 text-dark-700 rounded-lg text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-1 focus:ring-dark-400/50"
                  >
                    <PlusIcon className="h-4 w-4" />
                    New Chat
                  </button>
                )}
                <button
                  onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                  className="p-2 hover:bg-dark-200/60 rounded-lg transition-colors duration-200 text-dark-600 hover:text-dark-700"
                >
                  <svg className={`h-4 w-4 transition-transform duration-200 ${sidebarCollapsed ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Chat History */}
            {!sidebarCollapsed && (
              <div className="flex-1 overflow-y-auto px-4 pb-4">
                <div className="text-xs font-medium text-dark-600 mb-3">
                  Recent
                </div>
                <div className="space-y-1">
                  {chatSessions.length > 0 ? (
                    chatSessions.map((session, index) => (
                      <div 
                        key={session.session_id || index} 
                        className="p-3 rounded-lg bg-dark-200/40 hover:bg-dark-200/60 cursor-pointer transition-all duration-200 group"
                        onClick={async () => {
                          try {
                            // Load messages for this session
                            const sessionRes = await axios.get(createApiUrl(`api/chat/sessions/${session.session_id}`));
                            const sessionMessages = Array.isArray(sessionRes.data.history) ? sessionRes.data.history : [];
                            setMessages(sessionMessages);
                            setCurrentSessionId(session.session_id);
                          } catch (err) {
                            console.error('Failed to load session:', err);
                            setMessages([]); // Set empty array on error
                          }
                        }}
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-dark-700 truncate group-hover:text-dark-800 transition-colors">
                              {session.title || `Chat ${session.session_id}`}
                            </p>
                            <p className="text-xs text-dark-600 mt-1">
                              {session.message_count} message{session.message_count !== 1 ? 's' : ''}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="p-3 rounded-lg bg-dark-200/20 border border-dark-400/20 text-center">
                      <div className="text-sm text-dark-600">
                        No previous chats
                      </div>
                      <div className="text-xs text-dark-500 mt-1">
                        Start a new conversation below
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Collapsed Sidebar Icons */}
            {sidebarCollapsed && (
              <div className="flex-1 overflow-y-auto px-3 py-4">
                <div className="space-y-2 flex flex-col items-center">
                  <button
                    onClick={handleNewChat}
                    className="p-2 bg-dark-200/80 hover:bg-dark-300/80 text-dark-700 rounded-lg transition-all duration-200 focus:outline-none focus:ring-1 focus:ring-dark-400/50"
                    title="New Chat"
                  >
                    <PlusIcon className="h-4 w-4" />
                  </button>
                  <div className="w-full h-px bg-dark-400/20 my-2"></div>
                  {chatSessions.length > 0 ? (
                    chatSessions.slice(0, 3).map((session, index) => (
                      <button
                        key={session.session_id || index}
                        onClick={async () => {
                          try {
                            // Load messages for this session
                            const sessionRes = await axios.get(createApiUrl(`api/chat/sessions/${session.session_id}`));
                            const sessionMessages = Array.isArray(sessionRes.data.history) ? sessionRes.data.history : [];
                            setMessages(sessionMessages);
                            setCurrentSessionId(session.session_id);
                          } catch (err) {
                            console.error('Failed to load session:', err);
                            setMessages([]); // Set empty array on error
                          }
                        }}
                        className="p-2 bg-dark-200/40 hover:bg-dark-200/60 rounded-lg transition-all duration-200 focus:outline-none"
                        title={session.title || 'Untitled Chat'}
                      >
                        <ChatBubbleLeftRightIcon className="h-4 w-4 text-dark-600" />
                      </button>
                    ))
                  ) : (
                    <div className="p-2 rounded-lg bg-dark-200/20 border border-dark-400/20">
                      <ChatBubbleLeftRightIcon className="h-4 w-4 text-dark-500" />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Main Chat Interface */}
        <div className="flex-1 flex flex-col bg-transparent">
          {/* Chat Messages Area */}
          <div className="flex-1 overflow-y-auto">
            {(!messages || messages.length === 0) && !sending ? (
              <div className="flex flex-col items-center justify-center h-full px-6">
                <div className="text-center max-w-2xl">
                  <div className="flex items-center justify-center mb-6">
                    <div className="w-8 h-8 bg-gradient-to-br from-accent-primary to-accent-tertiary rounded-lg flex items-center justify-center shadow-sm mr-3">
                      <div className="text-white font-bold text-sm tracking-wider">H</div>
                    </div>
                    <h1 className="text-2xl font-light text-dark-800 tracking-tight">
                      Meet <span className="text-accent-primary font-medium">Harvy</span>
                    </h1>
                  </div>
                  
                  {/* Input form directly in the centered container */}
                  <form onSubmit={handleSubmit} className="relative w-full max-w-3xl">
                    <div className="relative">
                      <textarea
                        ref={inputRef}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder="Ask me anything..."
                        disabled={sending}
                        className="w-full border border-dark-400/30 rounded-2xl px-5 py-4 pr-14 bg-dark-200/80 backdrop-blur-sm text-dark-800 placeholder:text-dark-500 focus:outline-none focus:ring-1 focus:ring-accent-primary/50 focus:border-accent-primary/30 resize-none min-h-[3rem] max-h-[10rem] text-sm transition-all duration-200"
                        rows={1}
                        onInput={(e) => {
                          e.target.style.height = 'auto';
                          e.target.style.height = `${Math.min(e.target.scrollHeight, 160)}px`;
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSubmit(e);
                          }
                        }}
                      />
                      <button
                        type="submit"
                        disabled={sending || !input.trim()}
                        className="absolute right-3 bottom-3 p-2.5 bg-accent-primary text-white rounded-xl hover:bg-accent-tertiary disabled:opacity-40 transition-all duration-200 focus:outline-none"
                      >
                        <PaperAirplaneIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            ) : (
              <div className="px-6 py-8 max-w-4xl mx-auto">
                <div className="space-y-6">
                  {(messages || []).map((m, i) => (
                    <div key={i} className="group">
                      <div className={`flex gap-3 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        {m.role === 'assistant' && (
                          <div className="w-7 h-7 bg-gradient-to-br from-accent-primary to-accent-tertiary rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                            <div className="text-white font-bold text-xs">H</div>
                          </div>
                        )}
                        <div className={`max-w-[70%] ${m.role === 'user' ? 'order-2' : 'order-1'}`}>
                          <div className={`${m.role === 'user' 
                            ? 'bg-accent-primary text-white rounded-lg px-3 py-2' 
                            : 'bg-transparent text-slate-900 dark:text-dark-700'
                          }`}>
                            <div className={`prose prose-sm max-w-none leading-relaxed ${
                              m.role === 'user'
                                ? 'prose-invert text-white'
                                : 'prose-slate dark:prose-invert text-slate-900 dark:text-dark-700'
                            }`}>
                              {renderMessageContent(m)}
                            </div>
                          </div>
                        </div>
                        {m.role === 'user' && (
                          <div className="w-7 h-7 bg-dark-300 rounded-full flex items-center justify-center flex-shrink-0 mt-1 order-3">
                            <div className="text-dark-700 font-medium text-xs">U</div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  {sending && (
                    <div className="flex justify-center my-6">
                      <div className="flex items-center gap-2 bg-dark-300/60 backdrop-blur-sm rounded-full px-3 py-1.5">
                        <div className="w-4 h-4 bg-gradient-to-br from-accent-primary to-accent-tertiary rounded-lg flex items-center justify-center">
                          <div className="text-white font-bold text-xs tracking-wider">H</div>
                        </div>
                        <div className="flex items-center">
                          <span className="text-xs text-dark-600 font-medium mr-1">thinking</span>
                          <div className="flex gap-0.5">
                            <div className="w-1 h-1 bg-dark-600 rounded-full animate-pulse" style={{animationDelay: '0ms', animationDuration: '1.4s'}}></div>
                            <div className="w-1 h-1 bg-dark-600 rounded-full animate-pulse" style={{animationDelay: '0.2s', animationDuration: '1.4s'}}></div>
                            <div className="w-1 h-1 bg-dark-600 rounded-full animate-pulse" style={{animationDelay: '0.4s', animationDuration: '1.4s'}}></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              </div>
            )}
          </div>

          {/* Claude-style Input Area - Only for conversations with messages */}
          {messages && messages.length > 0 && (
            <div className="border-t border-dark-400/20 bg-dark-100/95 backdrop-blur-sm p-6">
              <div className="max-w-4xl mx-auto">
                <form onSubmit={handleSubmit} className="relative">
                  <div className="relative">
                    <textarea
                      ref={inputRef}
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      placeholder="Ask me anything about Harvard classes, requirements, or academic planning..."
                      disabled={sending}
                      className="w-full border border-dark-400/30 rounded-2xl px-5 py-4 pr-14 bg-dark-200/60 backdrop-blur-sm text-dark-800 placeholder:text-dark-500 focus:outline-none focus:ring-1 focus:ring-accent-primary/50 focus:border-accent-primary/30 resize-none min-h-[3rem] max-h-[10rem] text-sm transition-all duration-200"
                      rows={1}
                      onInput={(e) => {
                        e.target.style.height = 'auto';
                        e.target.style.height = `${Math.min(e.target.scrollHeight, 160)}px`;
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSubmit(e);
                        }
                      }}
                    />
                    <button
                      type="submit"
                      disabled={sending || !input.trim()}
                      className="absolute right-3 bottom-3 p-2.5 bg-accent-primary text-white rounded-xl hover:bg-accent-tertiary disabled:opacity-40 transition-all duration-200 focus:outline-none"
                    >
                      <PaperAirplaneIcon className="h-4 w-4" />
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>


      <CourseComparisonModal
        isOpen={compareModalOpen}
        onClose={() => setCompareModalOpen(false)}
        courses={coursesToCompare}
      />
    </div>
  );
}

export default Chat;